// =====================================================================
// crmCustomer — Opportunity Stakeholders tab
//
// StakeholderEditor — inline editor for the opportunity_contacts junction.
// Lists every contact at the deal's account; for each, shows their role on
// THIS deal (Champion / Economic Buyer / Influencer / Blocker / …) plus a
// confidence slider and a "primary" star, both stored on the junction row.
// Adding a stakeholder = upserting a row with role=null until set; removing
// = deleting the row. The list collapses unattached contacts into a section
// at the bottom so the engaged stakeholders sit on top.
//
// Split out from opportunities.jsx in the post-MEDDPICCR refactor — keep
// the load order in build.js + index.html so this loads before
// opportunities.js (StakeholderEditor is referenced by OppDetail there).
// =====================================================================

const STAKEHOLDER_ROLES = [
  "Champion",
  "Economic Buyer",
  "Technical Evaluator",
  "Decision Maker",
  "Influencer",
  "End User",
  "Blocker",
];

function StakeholderRoleBadge({ role }) {
  if (!role) return <span className="muted">—</span>;
  const tone = role === "Champion" ? "amber"
             : role === "Economic Buyer" ? "amber"
             : role === "Blocker" ? "red"
             : "slate";
  return <Badge tone={tone}>{role}</Badge>;
}

function StakeholderRow({ opp, contact, oc, navigate }) {
  const [busy, setBusy] = useState(false);
  async function setField(field, value) {
    setBusy(true);
    try {
      const payload = oc
        ? { role: oc.role, roleConfidence: oc.roleConfidence, isPrimary: oc.isPrimary, [field]: value }
        : { role: null, roleConfidence: null, isPrimary: false, [field]: value };
      await tpAPI.opportunityContacts.upsert(opp.id, contact.id, payload);
    } catch (e) {
      console.error("Failed to update stakeholder:", e);
    } finally {
      setBusy(false);
    }
  }
  async function detach() {
    setBusy(true);
    try { await tpAPI.opportunityContacts.remove(opp.id, contact.id); }
    catch (e) { console.error("Failed to remove stakeholder:", e); }
    finally { setBusy(false); }
  }
  const attached = !!oc;
  return (
    <tr style={{ cursor: "default", opacity: busy ? 0.6 : 1 }}>
      <td>
        <span className="tbl-link" onClick={() => navigate("sales/contacts/" + contact.id)}>
          {contact.first} {contact.last}
        </span>
        {oc && oc.isPrimary ? <span title="Primary contact" style={{ marginLeft: 6, color: "var(--amber, #b45309)" }}>★</span> : null}
      </td>
      <td><span className="muted">{contact.title}</span></td>
      <td>
        {attached ? (
          <select
            value={oc.role || ""}
            onChange={e => setField("role", e.target.value || null)}
            disabled={busy}
            style={{ fontSize: 12.5, padding: "2px 6px" }}
          >
            <option value="">— Role —</option>
            {STAKEHOLDER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        ) : (
          <span className="muted">Not on deal</span>
        )}
      </td>
      <td>
        {attached ? (
          <select
            value={oc.roleConfidence || ""}
            onChange={e => setField("roleConfidence", e.target.value ? Number(e.target.value) : null)}
            disabled={busy}
            style={{ fontSize: 12.5, padding: "2px 6px" }}
          >
            <option value="">—</option>
            <option value="1">1 — Low</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5 — High</option>
          </select>
        ) : null}
      </td>
      <td>
        {attached ? (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={!!oc.isPrimary}
              disabled={busy}
              onChange={e => setField("isPrimary", e.target.checked)}
            />
            <span className="muted">Primary</span>
          </label>
        ) : null}
      </td>
      <td>
        {attached
          ? <button className="btn btn-sm" onClick={detach} disabled={busy}>Remove</button>
          : <button className="btn btn-sm" onClick={() => setField("role", null)} disabled={busy}>+ Add to deal</button>}
      </td>
    </tr>
  );
}

function StakeholderEditor({ opp, stakeholders, accountName, navigate }) {
  // stakeholders: [{ contact, oc | null }] — passed in from OppDetail.
  const onDeal = stakeholders.filter(s => s.oc);
  const offDeal = stakeholders.filter(s => !s.oc);
  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Stakeholders</div>
        <div className="card-sub">{onDeal.length} on this deal · {offDeal.length} other contact{offDeal.length === 1 ? "" : "s"} at {accountName}</div>
      </div>
      <table className="tbl tbl-flush">
        <thead><tr><th>Name</th><th>Title</th><th>Role on this deal</th><th>Confidence</th><th></th><th></th></tr></thead>
        <tbody>
          {onDeal.map(({ contact, oc }) => (
            <StakeholderRow key={contact.id} opp={opp} contact={contact} oc={oc} navigate={navigate} />
          ))}
          {onDeal.length === 0 && (
            <tr><td colSpan={6} className="muted" style={{ textAlign: "center", padding: 12 }}>
              No stakeholders attached yet. Add contacts from the list below.
            </td></tr>
          )}
          {offDeal.length > 0 && (
            <tr><td colSpan={6} className="muted" style={{ background: "var(--surface-2, #f6f5f3)", padding: "8px 12px", fontSize: 12 }}>
              Other contacts at {accountName} — click <em>Add to deal</em> to attach.
            </td></tr>
          )}
          {offDeal.map(({ contact, oc }) => (
            <StakeholderRow key={contact.id} opp={opp} contact={contact} oc={oc} navigate={navigate} />
          ))}
        </tbody>
      </table>
      <div className="card-foot">
        {onDeal.length >= 2
          ? <>Multi-threaded · {onDeal.length} stakeholders on this deal.</>
          : <span style={{ color: "var(--red)" }}>Single-threaded risk: fewer than 2 stakeholders on this deal.</span>}
      </div>
    </div>
  );
}

window.STAKEHOLDER_ROLES = STAKEHOLDER_ROLES;
window.StakeholderRoleBadge = StakeholderRoleBadge;
window.StakeholderRow = StakeholderRow;
window.StakeholderEditor = StakeholderEditor;
